'use client';

import Image from "next/image";
import { useEffect, useRef, useState } from "react";

import { publicAsset } from "@/lib/public-assets";
import { cn } from "@/lib/utils";

type LandingFeedbackFigure = {
  src: string;
  width: number;
  height: number;
  alt: string;
  className: string;
};

const feedbackFigureSrc = (src: string) => {
  return src.startsWith("http") ? src : publicAsset(src);
};

export function LandingFeedbackFigures({
  figures,
}: {
  figures: LandingFeedbackFigure[];
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) {
      return;
    }

    if (!("IntersectionObserver" in window)) {
      setRevealed(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.35,
      },
    );

    observer.observe(root);

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={rootRef}>
      {figures.map((figure) => (
        <div
          key={figure.alt}
          data-reveal={revealed ? "true" : "false"}
          style={{
            width: figure.width,
            height: figure.height,
          }}
          className={cn(
            figure.className,
            "data-[reveal=true]:opacity-100",
          )}
        >
          <Image
            className="h-full w-full"
            src={feedbackFigureSrc(figure.src)}
            width={figure.width}
            height={figure.height}
            alt={figure.alt}
          />
        </div>
      ))}
    </div>
  );
}
